// [PURPOSE]: Reload the page when webpack reports a missing chunk after a deploy.
// [WHY]: Returning users hit "ChunkLoadError: Loading chunk N failed." because their
//        cached index.html references stale chunk hashes. A page reload fetches a
//        fresh index.html with current references. Loop guard prevents an infinite
//        reload if a chunk is genuinely 404 (e.g., bad deploy / CDN propagation).
// [INTEGRATION]: Batch 0.5 of the 2026-05-02 release plan. Wired in src/index.js.

const RELOAD_FLAG_KEY = 'tti.chunkLoadErrorReloadAt';
const LOOP_GUARD_MS = 5 * 60 * 1000;

function isChunkLoadError(error, message) {
  if (error && error.name === 'ChunkLoadError') return true;
  const text = (message || (error && error.message) || '').toString();
  return /Loading chunk [\w-]+ failed/i.test(text);
}

function safeRead(storage, key) {
  try {
    return storage ? storage.getItem(key) : null;
  } catch (_) {
    return null;
  }
}

function safeWrite(storage, key, value) {
  try {
    if (storage) storage.setItem(key, String(value));
  } catch (_) {
    /* swallow — storage may be disabled or full */
  }
}

/**
 * Install a global handler that detects ChunkLoadError events and reloads
 * the page (debounced by a 5-minute loop-guard window persisted in storage).
 *
 * @param {object} opts
 * @param {EventTarget} opts.target - usually window
 * @param {() => void} opts.reload - usually () => window.location.reload()
 * @param {Storage} [opts.storage] - usually window.sessionStorage
 * @param {() => number} [opts.now] - injected for tests; defaults to Date.now
 * @returns {() => void} uninstall fn that removes the listener
 */
function installChunkLoadErrorHandler({ target, reload, storage, now = () => Date.now() }) {
  if (!target || typeof target.addEventListener !== 'function') {
    return () => {};
  }

  const listener = (event) => {
    const error = event && event.error;
    const message = event && event.message;
    if (!isChunkLoadError(error, message)) return;

    const lastRaw = safeRead(storage, RELOAD_FLAG_KEY);
    const last = lastRaw ? Number(lastRaw) : 0;
    const current = now();
    if (last && current - last < LOOP_GUARD_MS) {
      // Reload already attempted within window; surrender to avoid loop.
      return;
    }

    safeWrite(storage, RELOAD_FLAG_KEY, current);
    try {
      reload();
    } catch (_) {
      /* swallow — caller's reload may throw in tests */
    }
  };

  target.addEventListener('error', listener);

  return function uninstall() {
    target.removeEventListener('error', listener);
  };
}

const __testHooks = { RELOAD_FLAG_KEY, LOOP_GUARD_MS };

export { installChunkLoadErrorHandler, __testHooks };
